import { type FC, useMemo } from 'react';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';
import { parseCookies } from 'nookies';

import config, { getFullLocale } from '@packages/config';
import { ErrorBoundary, HeadMeta } from '@packages/shared';
import { promotionBannerDataFetcher } from '@packages/modules/src/PromotionBanner/utils/promotionBannerDataFetcher';

import { getTopLevelCategories } from '@packages/search/src/utils/getTopLevelCategories';
import { API_URL, SEARCH_SEO_API_URL } from '@packages/search/src/api/rest/endpoints';
import { searchFetcher, searchSeoFetcher } from '@packages/search/src/api/rest/fetchers';
import {
  decodeSearchQuery,
  decodeSharedQueryParamsForRestApi,
} from '@packages/search/src/queryEncoding';
import { useSearchResult } from '@packages/search/src/hooks/useSearchResult';
import {
  PERSONALIZATION_COOKIE_NAME,
  usePersonalizationCookie,
} from '@packages/search/src/hooks/usePersonalizationCookie';
import { SearchApiRequestContext } from '@packages/search/src/SearchApiRequestContext';
import { extractTrackingInformation } from '@packages/search/src/utils/extractTrackingInformation';
import {
  SearchDebugPanel,
  SearchResultPageDefaultContent,
  SearchResultPageLoadingFallback,
  SearchResultPageZeroResults,
  SearchScrollTopButton,
  Tracking,
} from '@packages/search';
import { useRememberedPagedata } from '@packages/shared/src/hooks/useScrollRestoration';
import type { Device } from '@packages/themes/src/default';
import type { SearchApiRequestOptions } from '@packages/search/src/api/rest/types';
import { getChannelFromDevice } from '@packages/search/src/hooks/useChannel';
import { getMainAudience } from '@packages/cms-components';
import { getNitroIdFromCookies } from '@packages/search/src/utils/getNitroIdFromCookies';
import { localizedPathnameCacheKey } from '@packages/config/src/default';
import NewsletterSheet from '@packages/cms-components/src/components/Forms/NewsletterSheet/NewsletterSheet';
import { getEcState, logger, useConfig } from '@packages/utilities';

type CategoryPageProps = InferGetServerSidePropsType<typeof getServerSideProps>;
/**
 * Page for displaying search results
 * */
export const SearchResultPage: FC<CategoryPageProps> = ({
  topLevelCategories,
  seoResponse = {},
  alternateLinks,
}) => {
  usePersonalizationCookie();

  const { response, options, error } = useSearchResult();
  const { asPath } = useRouter();

  const {
    host: { domain },
  } = useConfig();

  if (error) {
    logger.error('No search result');
  }

  useRememberedPagedata();

  const requestCacheKey = response?.searchresult?.information?.requestCacheKey ?? 'missing';

  const searchApiContextValue = useMemo(() => ({ requestCacheKey }), [requestCacheKey]);

  const { title, description, keywords, robots, canonicallink } = seoResponse ?? {};

  // no canonicallink from SEO API response -> use current url without params as canonical
  const canonicalUrl =
    canonicallink ||
    `https://www.${domain}${
      asPath.split('?')[0].endsWith('/') ? asPath.split('?')[0] : asPath.split('?')[0].concat('/')
    }`.toLowerCase();

  const seoInformationExists = seoResponse && Object.keys(seoResponse).length > 0;
  const productCount = response?.searchresult?.result?.count;

  return (
    <SearchApiRequestContext.Provider value={searchApiContextValue}>
      {seoInformationExists && (
        <HeadMeta
          meta={{ title, description, keywords, robots }}
          links={{ canonical: canonicalUrl, alternateLinks }}
        />
      )}

      {response === undefined && <SearchResultPageLoadingFallback />}

      {response && productCount === 0 && (
        <SearchResultPageZeroResults topCategories={response.searchresult.result.topCategories} />
      )}

      {response && productCount && (
        <SearchResultPageDefaultContent
          topLevelCategories={topLevelCategories}
          apiResponse={response}
          seoResponse={seoResponse}
        />
      )}

      {response && (
        <Tracking pageType="serp" {...extractTrackingInformation(response, seoResponse)} />
      )}

      <SearchScrollTopButton />

      <SearchDebugPanel response={response} options={options} />
      <ErrorBoundary fallback={<span data-testid="newslettersheet-error" />}>
        <NewsletterSheet />
      </ErrorBoundary>
    </SearchApiRequestContext.Provider>
  );
};

// eslint-disable-next-line import/no-default-export
export default SearchResultPage;

export const getServerSideProps = (async (context) => {
  const { query, locale: language, req, res, resolvedUrl } = context;

  const { clientId } = config;

  const childLogger = logger.child({
    page: 'CategoryPage',
    function: 'getServerSideProps',
    resolvedUrl,
    query,
    language,
    clientId,
  });

  childLogger.trace('SearchResultPage.getServerSideProps started');

  const startTime = performance.now();
  const getTraceTimings = () => ({ elapsedMilliseconds: performance.now() - startTime });

  try {
    const searchQuery = decodeSearchQuery(query);

    if (!searchQuery) {
      throw new Error('invalid route match: required query param is missing');
    }

    const isBot = req?.headers?.['x-ua-bot'] !== undefined;
    const locale = getFullLocale(language);
    const cookies = parseCookies(context);
    const detectedDevice = req?.headers['x-ua-device'] as Device;

    const options: SearchApiRequestOptions = {
      locale,
      clientId,
      ...decodeSharedQueryParamsForRestApi(query),
      query: searchQuery,
      // optionally constrain search to a specific category
      category: typeof query.c === 'string' ? query.c : undefined,
      isBot,
      personalization: cookies[PERSONALIZATION_COOKIE_NAME],
      nitroId: getNitroIdFromCookies(cookies),
      lmPromo: typeof query.lmPromo === 'string' ? query.lmPromo : undefined,
      channel: getChannelFromDevice(detectedDevice),
    };

    const seoOptions = {
      locale,
      clientId,
      // It is important to have a plus sign here, because the search seo api expects it
      // to stay consistent with the "old" shop search
      query: searchQuery.replace(/ /g, '+'),
    };

    childLogger.trace(getTraceTimings(), 'starting fetch from APIs (EPS + SEO + promo banner)');

    const [searchPromise, seoPromise, promotionBannerPromise] = await Promise.allSettled([
      searchFetcher([API_URL, options]),
      searchSeoFetcher([SEARCH_SEO_API_URL, seoOptions]),
      promotionBannerDataFetcher(query, req.cookies, locale),
    ]);

    childLogger.trace(getTraceTimings(), 'completed fetch from APIs (EPS + SEO + promo banner)');

    if (searchPromise.status === 'rejected') {
      const { statusCode = 500 } = searchPromise.reason?.cause ?? {};

      if (statusCode === 404) {
        return { notFound: true };
      }

      childLogger.error({ requestInfo: { url: API_URL, payload: options } }, searchPromise.reason);

      throw new Error('Could not fetch search API results');
    }

    if (!searchPromise.value.searchresult) {
      childLogger.error(
        { requestInfo: { url: API_URL, payload: options }, response: searchPromise.value },
        'Search API response is missing searchresult',
      );

      throw new Error('Search API response is missing searchresult');
    }

    const searchResponse = searchPromise.value;

    // There are different reasons for a response without products
    // - DeepQuery with a bot request, to save costs DeepQuery is not executed for bots and instead zero results are returned
    // - search query contained blacklisted terms
    // - actual zero results case, search failed
    // in all cases we have to return a 410 to the bot, but a regular page to the user
    // NOTE: SEARCH-2403 conditional access is added because there seem to be circumstances where the searchresult object is missing the result property, despite being mandatory
    const hasNoProducts = searchResponse.searchresult.result?.count === 0;

    if (isBot && hasNoProducts) {
      if (searchResponse.searchresult.information.noDeepQueryBecauseOfBot) {
        res.statusCode = 410;
        res.end();

        childLogger.debug(
          `returning 410 to bot due to empty Search API response resulting from skipped DeepQuery`,
        );
      }

      // not always a 404 (depends on the check above), but it is the closest we can get with Next.js
      // the only special error-like return value that is supported by Next.js that does not lead to a 500 page is `{notFound: true}`
      // in theory we could also use a custom error page in conjunction with getServerSideProps there, but that adds a lot of overhead
      return {
        notFound: true,
      };
    }

    const { redirectUrl, redirectCode } = searchResponse.searchresult.information;

    if (redirectUrl && redirectCode) {
      childLogger.trace(
        { redirectUrl, redirectCode, ...getTraceTimings() },
        'executing EPS redirect',
      );

      return {
        redirect: {
          destination: redirectUrl,
          statusCode: redirectCode,
        },
      };
    }

    if (seoPromise.status === 'rejected') {
      childLogger.debug(
        { requestInfo: { url: SEARCH_SEO_API_URL, payload: seoOptions } },
        seoPromise.reason,
      );
    }

    if (promotionBannerPromise.status === 'rejected') {
      childLogger.debug(
        { description: 'Promotion Banner Data Fetch (Searchquery-Page)', searchQuery },
        'Could not fetch promo banner data',
      );
    }

    const searchResultCacheKey = 'search-api-result' as string;
    const audience = req.cookies ? getMainAudience(getEcState(req.cookies)) : 'anonymous';
    const promotionBannerCacheKey = `promotionBannerData-${language}-${audience}`;
    childLogger.trace(getTraceTimings(), 'retrieving L1 categories');

    const topLevelCategories = getTopLevelCategories();

    const alternateLinks = searchResponse.searchresult.result?.navigation?.alternates;

    childLogger.trace(
      getTraceTimings(),
      'SearchResultPage.getServerSideProps finished, returning props',
    );

    return {
      props: {
        fallback: {
          [searchResultCacheKey]: searchResponse,
          ...(promotionBannerPromise.status === 'fulfilled' && {
            [promotionBannerCacheKey]: promotionBannerPromise.value,
          }),
          ...(alternateLinks && {
            // NOTE: currently only a single alternate URL is supported for the locale switcher,
            // so even if there are multiple alternates, we only use the first one
            [localizedPathnameCacheKey]: Object.values(alternateLinks)[0],
          }),
        } as Record<string, any>,
        ...(topLevelCategories && { topLevelCategories }),
        ...(seoPromise.status === 'fulfilled' && {
          seoResponse: seoPromise.value,
        }),
        ...(alternateLinks && { alternateLinks }),
      },
    };
  } catch (error) {
    childLogger.error(error);
  }
  return {
    props: {
      fallback: {},
    },
  };
}) satisfies GetServerSideProps;
